import { safeAbsurd } from '../types/safeAbsurd';
import type { EdgeCenterLabel, EdgeLinecapLabel } from './constants';

export type EdgeLabelValue<Type extends EdgeCenterLabel | EdgeLinecapLabel> = {
  type: Type;
  visible?: boolean;
  text: string;
  wrapText?: boolean;
};

export type EdgeCenterLabelValue = EdgeLabelValue<EdgeCenterLabel>;
export type EdgeLinecapLabelValue = EdgeLabelValue<EdgeLinecapLabel>;

export type EdgeLabels = {
  source?: EdgeLinecapLabelValue[];
  center?: EdgeCenterLabelValue[];
  target?: EdgeLinecapLabelValue[];
};

export const getEdgeLinecapLabelText = (
  labelValues: EdgeLinecapLabelValue[],
): string | null => {
  const visibleValues = labelValues.filter(
    (l) => l.visible === true && l.text !== '',
  );
  if (visibleValues[0] === undefined) {
    return null;
  }

  // All LineCap labels on the edge have the same wrapText value
  const wrapText = visibleValues[0].wrapText;

  return visibleValues.map((label) => label.text).join(wrapText ? '\n' : '|');
};

export const getEdgeCenterLabelText = (
  labelValues: EdgeCenterLabelValue[],
): string | null => {
  const visibleValues = labelValues.filter(
    (l) => l.visible === true && l.text !== '',
  );
  if (visibleValues[0] === undefined) {
    return null;
  }

  // All Center labels on the edge have the same wrapText value
  const wrapText = visibleValues[0].wrapText;

  return visibleValues.map((label) => label.text).join(wrapText ? '\n' : '|');
};

export const getEdgeLabelText = (
  labels: EdgeLabels,
  key: keyof EdgeLabels,
): string | null => {
  const labelValues = labels[key];
  if (!labelValues) {
    return null;
  }

  switch (key) {
    case 'center':
      return getEdgeCenterLabelText(labelValues as EdgeCenterLabelValue[]);
    case 'source':
    case 'target':
      return getEdgeLinecapLabelText(labelValues as EdgeLinecapLabelValue[]);
    default:
      safeAbsurd(key, 'unknown label key');
      return null;
  }
};
